import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Landmark } from "lucide-react"
import { useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"

import { normalizarDecimal, paraElServidor } from "@/lib/decimales"

/**
 * Carga de un tributo en el expediente.
 *
 * ⚠ **No hay campo de tasa, y ésa es la garantía entera del paso 8.** La congela el
 * servidor con la del día de apertura del expediente: ni la de la cotización, que
 * puede tener semanas, ni la de hoy, que no está congelada y haría que el costo
 * histórico del despacho cambiara cada vez que alguien abre la pantalla. Ofrecer el
 * campo sería dejar elegir a qué precio costó algo que ya pasó, y `tasas_cambio` es
 * append-only: esa cifra quedaría para siempre.
 *
 * ⚠ **El catálogo se ofrece con un `datalist`, no con un `select`.** El catálogo
 * aprende del concepto que se teclea, así que cerrar la lista impediría cargar un
 * tributo nuevo — y proponer los conocidos es lo que evita «IVA», «I.V.A.» y «Iva»
 * como tres tributos que después ya no se pueden agrupar.
 *
 * ⚠ **Un impuesto no lleva proveedor.** Se le paga al fisco, y lo impone además el
 * `CHECK` de la tabla. Por eso este formulario no tiene ese campo, igual que
 * `PanelRubros` no tiene esa columna para un IMPUESTO.
 */

type ConceptoImpuesto = {
  id: number
  codigo: string
  nombre: string
}

export function FormularioImpuesto({ expedienteId }: { expedienteId: number }) {
  const clienteQuery = useQueryClient()
  const idLista = useId()
  const [concepto, setConcepto] = useState("")
  const [monto, setMonto] = useState("")

  const conceptos = useQuery({
    queryKey: ["conceptos-impuesto"],
    queryFn: () => api.get<ConceptoImpuesto[]>("/api/v1/conceptos-impuesto"),
  })

  const cargar = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/expedientes/${expedienteId}/impuestos`, {
        concepto,
        monto_usd: paraElServidor(monto),
      }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["rubros-expediente", expedienteId] })
      // El catálogo pudo aprender este concepto: se relee para que la próxima carga
      // lo proponga en vez de dejar que se teclee otra vez con otra grafía.
      clienteQuery.invalidateQueries({ queryKey: ["conceptos-impuesto"] })
      // Un tributo nuevo puede volver a bloquear el cierre.
      clienteQuery.invalidateQueries({ queryKey: ["bloqueos-de-cierre", expedienteId] })
      setConcepto("")
      setMonto("")
    },
  })

  const listo = concepto.trim().length > 0 && Number(normalizarDecimal(monto) ?? 0) > 0

  return (
    <form
      aria-label="Cargar un impuesto"
      onSubmit={(e) => {
        e.preventDefault()
        if (listo) cargar.mutate()
      }}
      className="superficie p-4"
    >
      <p className="flex items-center gap-2 text-sm font-medium">
        <Landmark size={16} aria-hidden={true} />
        Cargar un impuesto
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="flex-1 text-sm">
          Concepto
          <input
            list={idLista}
            className="campo mt-1"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
          />
          {/* Sugiere, no cierra: el catálogo aprende de lo que se teclea. */}
          <datalist id={idLista}>
            {(conceptos.data ?? []).map((c) => (
              <option key={c.id} value={c.nombre} />
            ))}
          </datalist>
        </label>

        <label className="text-sm">
          Monto en USD
          <input
            inputMode="decimal"
            className="campo mt-1 w-32 text-right tabular-nums"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
          />
        </label>

        <button
          type="submit"
          disabled={!listo || cargar.isPending}
          className="rounded-[var(--radius-control)] px-3 py-2 text-sm disabled:opacity-50"
          style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
        >
          {cargar.isPending ? "Cargando…" : "Cargar"}
        </button>
      </div>

      <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
        Se registra en bolívares a la tasa del día de apertura del expediente. Si no hay tasa para
        esa fecha, el rubro queda en dólares hasta que alguien la registre — no se graba un cero,
        que parecería gratis.
      </p>

      {cargar.isError && (
        <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
          {cargar.error instanceof ErrorApi
            ? cargar.error.message
            : "No se pudo cargar el impuesto."}
        </p>
      )}
    </form>
  )
}
